import { RADIUS, SPACE, T } from '@/design/tokens';
import { useDocumentImage } from '@/hooks/useDocumentImage';

interface DocumentImageProps {
  vehicleId: string;
  /** SHA-256 / storage ref for the document image. */
  imageRef: string;
  alt?: string;
}

/**
 * Fetches and renders a redacted invoice/document image from gg-api.
 * Handles loading, not-found (404), and error states gracefully.
 * The underlying object URL is revoked on unmount by useDocumentImage.
 */
export function DocumentImage({ vehicleId, imageRef, alt = 'Invoice image' }: DocumentImageProps) {
  const img = useDocumentImage(vehicleId, imageRef);

  const wrapStyle: React.CSSProperties = {
    marginTop: SPACE.md,
    borderRadius: RADIUS.md,
    border: `1px solid ${T.border}`,
    overflow: 'hidden',
    background: T.surface,
  };

  if (img.status === 'loading') {
    return (
      <div style={{ ...wrapStyle, padding: SPACE.md, color: T.textMuted, fontSize: 13 }}>
        Loading photo…
      </div>
    );
  }

  if (img.status === 'not_found') {
    return (
      <div
        style={{
          ...wrapStyle,
          padding: SPACE.md,
          color: T.textMuted,
          fontSize: 13,
          fontStyle: 'italic',
        }}
      >
        No photo on file.
      </div>
    );
  }

  if (img.status === 'error') {
    return (
      <div style={{ ...wrapStyle, padding: SPACE.md, color: T.textMuted, fontSize: 13 }}>
        Could not load photo.
      </div>
    );
  }

  if (img.status === 'ready') {
    if (img.contentType.startsWith('application/pdf')) {
      return (
        <div style={wrapStyle}>
          <object
            data={img.url}
            type="application/pdf"
            style={{ display: 'block', width: '100%', height: 600 }}
            aria-label={alt}
          >
            {/* Fallback for browsers that won't inline-render PDFs */}
            <div style={{ padding: SPACE.md, color: T.textMuted, fontSize: 13 }}>
              <a href={img.url} target="_blank" rel="noopener noreferrer">
                Open redacted PDF
              </a>
            </div>
          </object>
        </div>
      );
    }

    return (
      <div style={wrapStyle}>
        <img src={img.url} alt={alt} style={{ display: 'block', width: '100%', height: 'auto' }} />
      </div>
    );
  }

  // idle — no ref provided
  return null;
}
